/**
 * PDF Viewer Component
 * Uses the browser's native PDF viewer via blob URLs for full-featured viewing
 * (zoom, search, page navigation, print, download)
 */

import { useState, useEffect, useRef } from 'react';
import { useApp } from '../../../store/AppContext';
import { api } from '../../../services/api';
import { EmptyState } from '../../../components/common/EmptyState';
import './PdfViewer.css';

// In-memory cache so switching between PDFs doesn't re-download
const pdfBlobCache = new Map<string, string>();

export function PdfViewer() {
  const { state, updateProject, setStatus } = useApp();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevUrlRef = useRef<string | null>(null);

  const project = state.currentProject;
  const pdfs = project?.pdfs ?? [];
  const selectedPdfId = project?.preferences?.selectedPdfId;
  const selectedPdf = pdfs.find(p => p.id === selectedPdfId) ?? pdfs[0];

  // Load PDF and create blob URL
  useEffect(() => {
    if (!selectedPdf?.path) {
      setBlobUrl(null);
      return;
    }

    let cancelled = false;

    const loadPdf = async () => {
      // Check blob cache first
      const cached = pdfBlobCache.get(selectedPdf.path);
      if (cached) {
        setBlobUrl(cached);
        return;
      }

      setIsLoading(true);
      setError(null);
      setStatus('Loading PDF...');

      try {
        const data = await api.file.readBinary(selectedPdf.path);

        if (cancelled) return;

        const blob = new Blob([data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        pdfBlobCache.set(selectedPdf.path, url);
        setBlobUrl(url);
        setStatus('PDF loaded');
      } catch (err) {
        console.error('[PdfViewer] Failed to load PDF:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
          setStatus('Failed to load PDF');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [selectedPdf?.path, setStatus]);

  // Track current URL for cleanup on unmount
  useEffect(() => {
    prevUrlRef.current = blobUrl;
  }, [blobUrl]);

  // Clean up all blob URLs on unmount
  useEffect(() => {
    return () => {
      pdfBlobCache.forEach((url) => URL.revokeObjectURL(url));
      pdfBlobCache.clear();
    };
  }, []);

  const handleSelectPdf = async (pdfId: string) => {
    if (project) {
      await updateProject(project.id, {
        preferences: {
          ...project.preferences,
          selectedPdfId: pdfId,
        },
      });
    }
  };

  if (pdfs.length === 0) {
    return (
      <div className="pdf-viewer">
        <EmptyState
          icon="file"
          title="No PDFs"
          description="Add a PDF document to view alongside your data"
        />
      </div>
    );
  }

  return (
    <div className="pdf-viewer">
      {pdfs.length > 1 && (
        <div className="pdf-viewer__toolbar">
          <select
            className="pdf-viewer__select"
            value={selectedPdf?.id ?? ''}
            onChange={e => handleSelectPdf(e.target.value)}
          >
            {pdfs.map(pdf => (
              <option key={pdf.id} value={pdf.id}>
                {pdf.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="pdf-viewer__content">
        {isLoading ? (
          <div className="pdf-viewer__loading">
            <div className="pdf-viewer__spinner" />
            <span>Loading PDF...</span>
          </div>
        ) : error ? (
          <div className="pdf-viewer__error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <span>{error}</span>
          </div>
        ) : blobUrl ? (
          <iframe
            key={blobUrl}
            src={blobUrl}
            className="pdf-viewer__iframe"
            title={selectedPdf?.name ?? 'PDF'}
          />
        ) : null}
      </div>
    </div>
  );
}
